import {
  useEffect,
  useRef,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { Link } from "react-router-dom";
import ProjectCard from "@/components/feature/ProjectCard";
import type { Project } from "@/lib/types";

interface ProjectCarousel3DProps {
  projects: Project[];
  animated?: boolean;
}

type P3DStyle = CSSProperties & Record<string, string | number>;

const BASE_SPEED = 18; // grados por segundo ≈ 20s por vuelta
const SLOW_SPEED = 2.5; // velocidad al pasar el mouse
const REDUCED_SPEED = 1.5; // velocidad con prefers-reduced-motion

interface CardProps {
  project: Project;
  index: number;
  quantity: number;
  tzFactor: number;
}

function ProjectCard3D({ project, index, quantity, tzFactor }: CardProps) {
  const slug = project.slug || project.id;
  return (
    <div
      className="p3d-card group cursor-pointer border border-background-200/80 bg-background-50"
      style={
        {
          "--index": index,
          "--quantity": quantity,
          "--translateZ": `calc(var(--w) * ${tzFactor})`,
        } as P3DStyle
      }
    >
      {project.imagen ? (
        <img
          src={project.imagen}
          alt={`${project.nombre} - ${project.categoria || ""}`}
          title={`${project.nombre} - ${project.categoria || ""}`}
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary-100 to-accent-100 text-primary-400">
          <i className="ri-image-line text-4xl" />
        </div>
      )}

      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/15 to-transparent" />

      {project.url && (
        <a
          href={project.url}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute top-3 right-3 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-background-50/90 text-foreground-800 opacity-0 backdrop-blur transition-opacity group-hover:opacity-100 hover:bg-primary-500 hover:text-background-50 cursor-pointer"
          aria-label={`Abrir sitio de ${project.nombre}`}
        >
          <i className="ri-external-link-line text-sm" />
        </a>
      )}

      <div className="absolute inset-x-0 bottom-0 p-3.5 md:p-4">
        {project.categoria && (
          <span className="mb-1.5 inline-block rounded-full bg-primary-500 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-background-50">
            {project.categoria}
          </span>
        )}
        <div className="flex items-end justify-between gap-2">
          <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-white md:text-base">
            {project.nombre}
          </h3>
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-background-50/90 text-primary-600 opacity-0 transition-all group-hover:opacity-100">
            <i className="ri-arrow-right-line text-sm" />
          </span>
        </div>
      </div>

      <Link
        to={`/proyecto/${slug}`}
        className="absolute inset-0 z-10"
        aria-label={`Ver proyecto ${project.nombre}`}
      />
    </div>
  );
}

export default function ProjectCarousel3D({
  projects,
  animated = true,
}: ProjectCarousel3DProps) {
  const innerRef = useRef<HTMLDivElement>(null);
  const angleRef = useRef(0);
  const speedRef = useRef(BASE_SPEED);
  const targetRef = useRef(BASE_SPEED);
  const draggingRef = useRef(false);
  const didDragRef = useRef(false);
  const lastXRef = useRef(0);

  const quantity = projects.length;
  const single = quantity === 1;
  const tzFactor = quantity > 1 ? 0.5 / Math.tan(Math.PI / quantity) + 0.22 : 0;

  useEffect(() => {
    if (single || !animated) {
      if (innerRef.current) {
        innerRef.current.style.transform =
          "perspective(1000px) rotateX(-15deg) rotateY(0deg)";
      }
      return;
    }

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let raf = 0;
    let last = performance.now();

    const loop = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const target = draggingRef.current ? 0 : reduced ? REDUCED_SPEED : targetRef.current;
      speedRef.current += (target - speedRef.current) * Math.min(1, dt * 3);
      angleRef.current = (angleRef.current + speedRef.current * dt) % 360;
      if (innerRef.current) {
        innerRef.current.style.transform = `perspective(1000px) rotateX(-15deg) rotateY(${angleRef.current}deg)`;
      }
      raf = requestAnimationFrame(loop);
    };

    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [single, animated]);

  const handlePointerEnter = () => {
    if (window.matchMedia("(hover: hover)").matches) {
      targetRef.current = SLOW_SPEED;
    }
  };

  const handlePointerLeave = () => {
    targetRef.current = BASE_SPEED;
  };

  const handlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    draggingRef.current = true;
    didDragRef.current = false;
    lastXRef.current = e.clientX;
  };

  const handlePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    const dx = e.clientX - lastXRef.current;
    lastXRef.current = e.clientX;
    if (Math.abs(dx) > 3) didDragRef.current = true;
    angleRef.current += dx * 0.4;
  };

  const handlePointerUp = () => {
    draggingRef.current = false;
    targetRef.current = BASE_SPEED;
  };

  const handleClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (didDragRef.current) {
      e.preventDefault();
      e.stopPropagation();
      didDragRef.current = false;
    }
  };

  if (quantity === 0) return null;

  if (single) {
    return (
      <div className="flex justify-center py-2">
        <div className="w-full max-w-sm">
          <ProjectCard project={projects[0]} />
        </div>
      </div>
    );
  }

  return (
    <div
      className="p3d-wrapper h-[360px] sm:h-[420px] md:h-[480px] lg:h-[560px] [--w:170px] [--h:230px] sm:[--w:190px] sm:[--h:260px] md:[--w:230px] md:[--h:310px] lg:[--w:270px] lg:[--h:360px]"
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onClickCapture={handleClickCapture}
      role="group"
      aria-label="Carrusel de proyectos"
    >
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="h-[70%] w-[70%] rounded-full bg-primary-500/10 blur-3xl" />
      </div>
      <div className="pointer-events-none absolute bottom-8 left-1/2 h-px w-[60%] -translate-x-1/2 bg-gradient-to-r from-transparent via-primary-300/40 to-transparent" />

      <div ref={innerRef} className="p3d-inner">
        {projects.map((project, i) => (
          <ProjectCard3D
            key={project.id}
            project={project}
            index={i}
            quantity={quantity}
            tzFactor={tzFactor}
          />
        ))}
      </div>
    </div>
  );
}